import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';
import { channelMeta } from '../utils/channels';

// Why a quote was, or was not, sent.
//
// The reasons were always computed and then written to a log line that had
// rotated by the time anyone asked. This is that data, grouped — because the
// point is not to read individual failures, it's to see which ONE reason is
// costing the most quotes this week.

const DAY_PRESETS = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
];

function msLabel(ms) {
  if (ms === null || ms === undefined) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

export default function QuoteInsights() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [range, setRange] = useState({ from: '', to: '' });
  const [selectedReason, setSelectedReason] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (range.from && range.to) {
      p.set('from_date', range.from);
      p.set('to_date', range.to);
    } else {
      p.set('days', String(days));
    }
    return p;
  }, [days, range]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await apiClient.get(`/api/v1/quote-insights/stats?${params}`);
      setStats(data);
    } catch (e) {
      // Distinct from "no data": telling someone nothing happened when the
      // request actually failed is worse than telling them nothing at all.
      setLoadError(true);
      toast.error('Could not load quote insights');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openReason = async (reason) => {
    if (selectedReason === reason) {
      setSelectedReason(null);
      setAttempts(null);
      return;
    }
    setSelectedReason(reason);
    setLoadingAttempts(true);
    try {
      const p = new URLSearchParams(params);
      p.set('reason_code', reason);
      const { data } = await apiClient.get(`/api/v1/quote-insights/attempts?${p}`);
      setAttempts(data);
    } catch (e) {
      toast.error('Could not load those attempts');
    } finally {
      setLoadingAttempts(false);
    }
  };

  const totals = stats?.totals || {};
  const worst = (stats?.by_reason || [])[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Quote insights</h1>
          <p className="text-sm text-gray-500">
            Every quote decision, and the reason behind it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {DAY_PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => { setRange({ from: '', to: '' }); setDays(p.days); }}
              className={`px-3 py-1.5 rounded text-sm border ${
                !range.from && days === p.days
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
          <input
            type="date" value={range.from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date" value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-500">Loading…</div>
      ) : loadError ? (
        <div className="bg-white border border-red-200 rounded-lg p-10 text-center">
          <p className="text-red-700 font-medium">Couldn't load quote insights.</p>
          <button onClick={fetchStats}
                  className="mt-3 px-3 py-1.5 text-sm rounded bg-indigo-600 text-white">
            Try again
          </button>
        </div>
      ) : !stats || totals.attempts === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
          <p className="text-gray-700 font-medium">No quote decisions in this period.</p>
          <p className="text-sm text-gray-500 mt-1">
            Decisions are recorded from the moment a quote conversation is
            identified, so an empty period means no quote requests came in.
          </p>
        </div>
      ) : (
        <>
          {/* The number worth glancing at daily. */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Sent rate</div>
              <div className="text-3xl font-semibold text-gray-900 mt-1">
                {totals.sent_rate === null ? '—' : `${totals.sent_rate}%`}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {totals.sent} of {totals.attempts} quote decisions
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Quotes sent</div>
              <div className="text-3xl font-semibold text-emerald-600 mt-1">{totals.sent}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Not sent</div>
              <div className="text-3xl font-semibold text-yellow-600 mt-1">{totals.not_sent}</div>
              {worst && (
                <div className="text-xs text-gray-500 mt-1 truncate" title={worst.label}>
                  Mostly: {worst.label}
                </div>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Time to quote</div>
              <div className="text-3xl font-semibold text-gray-900 mt-1">
                {msLabel(stats.timing_ms?.p50)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                median · p90 {msLabel(stats.timing_ms?.p90)} · slowest {msLabel(stats.timing_ms?.max)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Ordering IS the prioritisation: the top row is the one to fix. */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Why quotes didn't go out</h2>
                <p className="text-xs text-gray-500">
                  Commonest first. Click a row to see the conversations behind it.
                </p>
              </div>
              {(stats.by_reason || []).length === 0 ? (
                <div className="p-6 text-sm text-gray-500">
                  Every quote in this period was sent.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {(stats.by_reason || []).map((r) => {
                    const pct = totals.not_sent
                      ? Math.round((r.count / totals.not_sent) * 100) : 0;
                    return (
                      <li key={r.reason_code}>
                        <button
                          onClick={() => openReason(r.reason_code)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-sm font-medium text-gray-900">{r.label}</span>
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                              {r.count} · {pct}%
                            </span>
                          </div>
                          <div className="mt-1.5 h-1.5 bg-gray-100 rounded">
                            <div className="h-1.5 bg-indigo-500 rounded" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="mt-1 text-xs text-gray-400 font-mono">{r.reason_code}</div>
                        </button>

                        {selectedReason === r.reason_code && (
                          <div className="px-4 pb-4">
                            {loadingAttempts ? (
                              <div className="text-sm text-gray-500 py-3">Loading…</div>
                            ) : (
                              <div className="border border-gray-200 rounded overflow-x-auto">
                                <table className="min-w-full text-xs">
                                  <thead className="bg-gray-50 text-gray-500">
                                    <tr>
                                      <th className="px-2 py-1.5 text-left">When</th>
                                      <th className="px-2 py-1.5 text-left">Channel</th>
                                      <th className="px-2 py-1.5 text-left">Reference</th>
                                      <th className="px-2 py-1.5 text-left">Vehicle</th>
                                      <th className="px-2 py-1.5 text-left">Area</th>
                                      <th className="px-2 py-1.5 text-left">Detail</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {(attempts?.items || []).map((a) => {
                                      const meta = channelMeta(a.channel);
                                      return (
                                        <tr key={a.id} className="hover:bg-gray-50">
                                          <td className="px-2 py-1.5 whitespace-nowrap text-gray-500">
                                            {new Date(a.created_at).toLocaleString('en-GB', {
                                              day: '2-digit', month: 'short',
                                              hour: '2-digit', minute: '2-digit' })}
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.classes}`}>
                                              {meta.label}
                                            </span>
                                          </td>
                                          {/* Opens the conversation behind the row. Without this the
                                              dashboard says a quote didn't go out and gives you no way
                                              to go and read why. Searched by reference, which the
                                              Responses listing matches against freshdesk_ticket_id —
                                              so "wa:2771…" and "131311" both land. */}
                                          <td className="px-2 py-1.5 font-mono">
                                            {a.ticket_ref ? (
                                              <a
                                                href={`/responses?search=${encodeURIComponent(a.ticket_ref)}`}
                                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                                title="Open this conversation"
                                              >
                                                {a.ticket_ref}
                                              </a>
                                            ) : (
                                              <span className="text-gray-400">—</span>
                                            )}
                                          </td>
                                          <td className="px-2 py-1.5 text-gray-700">{a.vehicle || '—'}</td>
                                          <td className="px-2 py-1.5 text-gray-700">{a.locality || '—'}</td>
                                          <td className="px-2 py-1.5 text-gray-600">{a.reason_detail || '—'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                {attempts && attempts.total > (attempts.items || []).length && (
                                  <div className="px-2 py-1.5 text-[11px] text-gray-500 bg-gray-50">
                                    Showing {attempts.items.length} of {attempts.total}.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">By channel</h2>
                <p className="text-xs text-gray-500">
                  Both channels use the same reason codes, so these compare directly.
                </p>
              </div>
              <ul className="divide-y divide-gray-100">
                {(stats.by_channel || []).map((c) => {
                  const meta = channelMeta(c.channel);
                  const tot = c.sent + c.not_sent;
                  const rate = tot ? Math.round((c.sent / tot) * 100) : 0;
                  return (
                    <li key={c.channel} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.classes}`}>
                          {meta.label}
                        </span>
                        <span className="text-sm text-gray-700">{rate}% sent</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {c.sent} sent · {c.not_sent} not sent
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
